import type { KeyboardShortcut } from "@/types/workspace";

export const KEYBOARD_SHORTCUTS_STORAGE_KEY = "lytick-keyboard-shortcuts";

export const DEFAULT_SHORTCUTS: KeyboardShortcut[] = [
  {
    id: "close-panels",
    name: "Close panels",
    description: "Close open drawers, overlays, and selections",
    defaultKey: "Escape",
    customKey: null,
    customizable: false,
  },
  {
    id: "show-shortcuts",
    name: "Show shortcuts",
    description: "Open the keyboard shortcuts overlay",
    defaultKey: "Ctrl+/",
    customKey: null,
    customizable: false,
  },
  {
    id: "command-palette",
    name: "Command palette",
    description: "Open the command palette to search for any action",
    defaultKey: "Ctrl+K",
    customKey: null,
    customizable: true,
  },
  {
    id: "next-focus",
    name: "Next focus",
    description: "Move to the next focus target",
    defaultKey: "Tab",
    customKey: null,
    customizable: false,
  },
  {
    id: "previous-focus",
    name: "Previous focus",
    description: "Move to the previous focus target",
    defaultKey: "Shift+Tab",
    customKey: null,
    customizable: false,
  },
  {
    id: "activate-focus",
    name: "Activate focused control",
    description: "Activate the currently focused control",
    defaultKey: "Enter / Space",
    customKey: null,
    customizable: false,
  },
  {
    id: "timeframe-1",
    name: "Switch timeframe to 1m",
    description: "Set the primary chart timeframe to 1m",
    defaultKey: "1",
    customKey: null,
    customizable: true,
  },
  {
    id: "timeframe-2",
    name: "Switch timeframe to 5m",
    description: "Set the primary chart timeframe to 5m",
    defaultKey: "2",
    customKey: null,
    customizable: true,
  },
  {
    id: "timeframe-3",
    name: "Switch timeframe to 15m",
    description: "Set the primary chart timeframe to 15m",
    defaultKey: "3",
    customKey: null,
    customizable: true,
  },
  {
    id: "timeframe-4",
    name: "Switch timeframe to 1H",
    description: "Set the primary chart timeframe to 1H",
    defaultKey: "4",
    customKey: null,
    customizable: true,
  },
  {
    id: "timeframe-5",
    name: "Switch timeframe to 4H",
    description: "Set the primary chart timeframe to 4H",
    defaultKey: "5",
    customKey: null,
    customizable: true,
  },
  {
    id: "timeframe-6",
    name: "Switch timeframe to 1D",
    description: "Set the primary chart timeframe to 1D",
    defaultKey: "6",
    customKey: null,
    customizable: true,
  },
  {
    id: "timeframe-7",
    name: "Switch timeframe to 1W",
    description: "Set the primary chart timeframe to 1W",
    defaultKey: "7",
    customKey: null,
    customizable: true,
  },
  {
    id: "save-template",
    name: "Save current template",
    description: "Save the current workspace as a custom template",
    defaultKey: "Ctrl+S",
    customKey: null,
    customizable: true,
  },
  {
    id: "switch-template-1",
    name: "Switch to template 1",
    description: "Apply the first template in the selector",
    defaultKey: "Ctrl+1",
    customKey: null,
    customizable: true,
  },
  {
    id: "switch-template-2",
    name: "Switch to template 2",
    description: "Apply the second template in the selector",
    defaultKey: "Ctrl+2",
    customKey: null,
    customizable: true,
  },
  {
    id: "switch-template-3",
    name: "Switch to template 3",
    description: "Apply the third template in the selector",
    defaultKey: "Ctrl+3",
    customKey: null,
    customizable: true,
  },
  {
    id: "zoom-in",
    name: "Zoom in",
    description: "Zoom the chart in",
    defaultKey: "+",
    customKey: null,
    customizable: true,
  },
  {
    id: "zoom-out",
    name: "Zoom out",
    description: "Zoom the chart out",
    defaultKey: "-",
    customKey: null,
    customizable: true,
  },
  // Drawing actions
  {
    id: "undo-drawing",
    name: "Undo",
    description: "Undo the last drawing action",
    defaultKey: "Ctrl+Z",
    customKey: null,
    customizable: false,
  },
  {
    id: "redo-drawing",
    name: "Redo",
    description: "Redo the last undone drawing action",
    defaultKey: "Ctrl+Shift+Z",
    customKey: null,
    customizable: false,
  },
  {
    id: "select-all-drawings",
    name: "Select all drawings",
    description: "Select all drawings on the chart",
    defaultKey: "Ctrl+A",
    customKey: null,
    customizable: false,
  },
  {
    id: "duplicate-drawing",
    name: "Duplicate drawing",
    description: "Duplicate the selected drawing",
    defaultKey: "Ctrl+D",
    customKey: null,
    customizable: false,
  },
  {
    id: "delete-drawing",
    name: "Delete drawing",
    description: "Delete the selected drawing or cancel active tool",
    defaultKey: "Delete / Backspace",
    customKey: null,
    customizable: false,
  },
  // Chart navigation
  {
    id: "jump-to-latest",
    name: "Jump to latest",
    description: "Scroll chart to the most recent candle",
    defaultKey: "End",
    customKey: null,
    customizable: false,
  },
  {
    id: "jump-to-oldest",
    name: "Jump to oldest",
    description: "Scroll chart to the oldest available candle",
    defaultKey: "Home",
    customKey: null,
    customizable: false,
  },
  {
    id: "scroll-chart",
    name: "Scroll chart",
    description: "Pan the chart left or right",
    defaultKey: "← →",
    customKey: null,
    customizable: false,
  },
  {
    id: "zoom-chart",
    name: "Zoom chart",
    description: "Zoom the chart in or out",
    defaultKey: "↑ ↓",
    customKey: null,
    customizable: false,
  },
  {
    id: "fullscreen",
    name: "Fullscreen",
    description: "Toggle fullscreen mode",
    defaultKey: "Shift+F",
    customKey: null,
    customizable: false,
  },
  // Replay
  {
    id: "replay-play-pause",
    name: "Play / Pause replay",
    description: "Toggle replay playback",
    defaultKey: "Space",
    customKey: null,
    customizable: false,
  },
  // Drawing tool shortcuts (Alt+key)
  {
    id: "tool-horizontal",
    name: "Horizontal line tool",
    description: "Activate the horizontal line drawing tool",
    defaultKey: "Alt+H",
    customKey: null,
    customizable: false,
  },
  {
    id: "tool-trend",
    name: "Trend line tool",
    description: "Activate the trend line drawing tool",
    defaultKey: "Alt+T",
    customKey: null,
    customizable: false,
  },
  {
    id: "tool-rectangle",
    name: "Rectangle tool",
    description: "Activate the rectangle drawing tool",
    defaultKey: "Alt+R",
    customKey: null,
    customizable: false,
  },
  {
    id: "tool-fibonacci",
    name: "Fibonacci tool",
    description: "Activate the Fibonacci retracement tool",
    defaultKey: "Alt+F",
    customKey: null,
    customizable: false,
  },
  {
    id: "tool-vertical",
    name: "Vertical line tool",
    description: "Activate the vertical line drawing tool",
    defaultKey: "Alt+V",
    customKey: null,
    customizable: false,
  },
  {
    id: "tool-text",
    name: "Text tool",
    description: "Activate the text annotation tool",
    defaultKey: "Alt+N",
    customKey: null,
    customizable: false,
  },
  {
    id: "tool-channel",
    name: "Channel tool",
    description: "Activate the price channel drawing tool",
    defaultKey: "Alt+C",
    customKey: null,
    customizable: false,
  },
  {
    id: "tool-risk-reward",
    name: "Risk:Reward tool",
    description: "Activate the risk/reward box tool",
    defaultKey: "Alt+Q",
    customKey: null,
    customizable: false,
  },
];

function sanitizeModifierSegment(segment: string) {
  const lowerSegment = segment.trim().toLowerCase();

  switch (lowerSegment) {
    case "cmd":
    case "meta":
      return "Meta";
    case "ctrl":
    case "control":
      return "Ctrl";
    case "alt":
    case "option":
      return "Alt";
    case "shift":
      return "Shift";
    default:
      return "";
  }
}

function sanitizeKeySegment(segment: string) {
  const value = segment.trim();

  switch (value.toLowerCase()) {
    case "esc":
      return "Escape";
    case "space":
    case "spacebar":
      return "Space";
    default:
      return value.length === 1 ? value.toUpperCase() : value;
  }
}

export function normalizeShortcutString(value: string) {
  return value
    .split("+")
    .map((segment, index, source) =>
      index < source.length - 1
        ? sanitizeModifierSegment(segment)
        : sanitizeKeySegment(segment),
    )
    .filter(Boolean)
    .join("+");
}

export function shortcutFromEvent(event: KeyboardEvent) {
  const parts: string[] = [];
  let includeShiftModifier = event.shiftKey;

  if (event.ctrlKey || event.metaKey) {
    parts.push("Ctrl");
  }

  if (event.altKey) {
    parts.push("Alt");
  }

  if (event.shiftKey) {
    parts.push("Shift");
  }

  let key = event.key;

  if (key === " ") {
    key = "Space";
  } else if (key === "?") {
    key = "/";
    includeShiftModifier = false;
  } else if (key === "_") {
    key = "-";
    includeShiftModifier = false;
  } else if (key === "+") {
    includeShiftModifier = false;
  } else if (key.length === 1) {
    key = key.toUpperCase();
  } else if (key === "Esc") {
    key = "Escape";
  }

  if (key === "Dead") {
    return "";
  }

  if (!includeShiftModifier) {
    const shiftIndex = parts.indexOf("Shift");

    if (shiftIndex >= 0) {
      parts.splice(shiftIndex, 1);
    }
  }

  parts.push(key);
  return normalizeShortcutString(parts.join("+"));
}

export function getShortcutBinding(shortcut: KeyboardShortcut) {
  return normalizeShortcutString(shortcut.customKey ?? shortcut.defaultKey);
}

export function loadKeyboardShortcuts() {
  if (typeof window === "undefined") {
    return DEFAULT_SHORTCUTS;
  }

  try {
    const rawValue = window.localStorage.getItem(KEYBOARD_SHORTCUTS_STORAGE_KEY);

    if (!rawValue) {
      return DEFAULT_SHORTCUTS;
    }

    const storedShortcuts = JSON.parse(rawValue) as KeyboardShortcut[];
    const byId = new Map(storedShortcuts.map((shortcut) => [shortcut.id, shortcut]));

    return DEFAULT_SHORTCUTS.map((shortcut) => {
      const storedValue = byId.get(shortcut.id);

      if (!storedValue) {
        return shortcut;
      }

      return {
        ...shortcut,
        customKey: storedValue.customKey,
      };
    });
  } catch {
    return DEFAULT_SHORTCUTS;
  }
}

export function saveKeyboardShortcuts(shortcuts: KeyboardShortcut[]) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    KEYBOARD_SHORTCUTS_STORAGE_KEY,
    JSON.stringify(shortcuts),
  );
  window.dispatchEvent(new CustomEvent("lytick:shortcuts-updated"));
}

export function resetKeyboardShortcuts() {
  saveKeyboardShortcuts(DEFAULT_SHORTCUTS);
}

export function findShortcutConflict(
  shortcuts: KeyboardShortcut[],
  binding: string,
  currentShortcutId?: string,
) {
  const normalizedBinding = normalizeShortcutString(binding);

  return shortcuts.find(
    (shortcut) =>
      shortcut.id !== currentShortcutId &&
      getShortcutBinding(shortcut) === normalizedBinding,
  );
}
